import React, { useEffect, useRef } from 'react';
import styled, { ThemedStyledProps, css } from 'styled-components';
import Icon from './Icon';

interface DropDownProps {
  options: Option[];
  onChange?: (val: any) => void;
  value: string;
  shadowRoot: any;
  popupPosition: string;
}

interface Option {
  label: string;
  value: string;
  icon?: any;
}

const Button = styled.button`
  background-color: #EFF4FA;
  border-radius: 4px;
  border: 0px;
  cursor: pointer;
  display: flex;
  width: 100%;
  padding: 10px;
  & > span {
    display: inline-block;
    font-size: 16px;
    align-self: center;
    white-space: nowrap;
  }
  & > svg {
    margin-left: 5px;
    margin-right: 8px;
    width: 16px;
    align-self: center;
  }
  & > img {
    margin-left: 5px;
    margin-right: 5px;
    width: 16px;
    align-self: center;
  }
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
`;

const UlStyled = styled.ul`
  position: absolute;
  list-style-type: none;
  width: 100%;
  padding: 0;
  z-index: 8000;

  border: 1px solid #999;
  cursor: pointer;

  & > li {
    text-align: left;
    padding: 3px 10px;
    font-size: 12px;
    display: flex;

    background-color: white;
    cursor: pointer;
    height: 26px;
  }

  & > li > svg {
    margin-right: 8px;
    align-self: center;
    width: 16px;
  }

  & > li > img {
    margin-right: 5px;
    align-self: center;
    width: 20px;
  }

  & > li > span {
    display: inline-block;
    align-self: center;
  }

  & > li:hover {
    background-color: #EFF4FA;
  }
`;

/**
 * Resolves display label from option value.
 * @param options - Available dropdown options.
 * @param val - Selected option value.
 * @returns Matching label or the raw value as fallback.
 */
const valToLabel = (options: Option[], val: string) => {
  const option = options.find(o => o.value === val);
  if (option) {
    return option.label;
  }
  return val;
};

/**
 * Resolves icon node for the currently selected value.
 * @param options - Available dropdown options.
 * @param val - Selected option value.
 * @returns Icon node or placeholder/null when unavailable.
 */
const valToIcon = (options: Option[], val: string) => {
  const option = options.find(o => o.value === val);
  if (option && option.icon) {
    return option.icon;
  }
  if (options.find(o => o.icon)) {
    return <img src="/assets/icons/leer.png" />;
  }
  return null;
};

/**
 * Custom dropdown control that works inside widget shadow roots.
 * @param props - Options, selected value, and selection handlers.
 * @returns Dropdown button with in-place option list.
 */
const Dropdown = ({ options, onChange, value, shadowRoot, popupPosition }: DropDownProps) => {
  const [open, setOpen] = React.useState(false);
  const ulRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (ulRef && ulRef.current) {
      // Shift list upward so it stays visible in constrained popup containers.
      ulRef.current.style.marginTop = -1 * (ulRef.current.clientHeight - ulRef.current.clientHeight / 3) + 'px';
    }
  }, [open]);

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (ulRef.current && !ulRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    // Bind the event listener
    shadowRoot.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      shadowRoot.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ulRef]);

  const handleOpen = () => {
    setOpen(!open);
  };

  return (
    <Container>
      <Button onClick={handleOpen}>
        {valToIcon(options, value)}
        <span>{valToLabel(options, value)}</span>
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
         {!open && <Icon name="chevronup" />} {open && <Icon name="chevrondown" />}{' '}
        </div>
      </Button>

      {open ? (
        <UlStyled ref={ulRef}>
          {options.map((menuItem, index) => (
            <li
              key={index}
              className="menu-item"
              onClick={() => {
                onChange ? onChange(menuItem.value) : null;
                setOpen(false);
              }}
            >
              {menuItem.icon} <span>{menuItem.label}</span>
            </li>
          ))}
        </UlStyled>
      ) : null}
    </Container>
  );
};

export default Dropdown;
